import { memo } from 'react';

interface AudioVisualizerBarsProps {
  audioLevels: number[];
  isActive: boolean;
}

// Number of bars to display around the button
const BAR_COUNT = 15;

// Minimum height for bars (when there's no audio)
const MIN_HEIGHT = 2;

// Maximum height for bars (at full volume)
const MAX_HEIGHT = 10;

/**
 * AudioVisualizerBars - Renders animated bars around a microphone button
 * that react to audio input levels.
 *
 * The bars are positioned in a circle around the center, with each bar
 * representing a different frequency band from the audio analysis.
 */
export const AudioVisualizerBars = memo(function AudioVisualizerBars({
  audioLevels,
  isActive
}: AudioVisualizerBarsProps) {
  if (!isActive) return null;

  // Calculate angle for each bar (evenly distributed in a circle)
  const angleStep = 360 / BAR_COUNT;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: BAR_COUNT }).map((_, index) => {
        // Get the audio level for this bar (cycling through available levels)
        const level = audioLevels[index % audioLevels.length] || 0;

        // Calculate bar height based on audio level
        const height = MIN_HEIGHT + level * (MAX_HEIGHT - MIN_HEIGHT);

        // Calculate rotation angle for this bar
        const angle = index * angleStep;

        // Distance from center to place the bar
        const radius = 18;

        return (
          <div
            key={index}
            className="absolute left-1/2 top-1/2"
            style={{
              transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${radius}px)`,
            }}
          >
            <div
              className="rounded-full bg-red-500 transition-all duration-75 ease-out"
              style={{
                width: '2px',
                height: `${height}px`,
                opacity: 0.7 + level * 0.3,
                transformOrigin: 'center bottom',
              }}
            />
          </div>
        );
      })}
    </div>
  );
});
